import React from "react";
import PropTypes from "prop-types";
import { Menu, MenuItem, Divider, ListItemIcon, ListItemText } from "@mui/material";
import { colors, customShadows, borderRadiusPx } from "../../../../theme";

/**
 * CommonMenu Component
 * Centralized, reusable dropdown menu adhering to the AutoBET theme palette and elevation tokens.
 *
 * Supports:
 * - Direct item configuration via `items` array:
 *   [{ label, onClick, icon, color, disabled, divider, component, to, href, selected, sx }]
 * - Standard composition with `children` (e.g. custom header boxes, Dividers, MenuItems)
 * - Automatic theme borders, rounded corners, and shadow elevation tokens
 */
const CommonMenu = ({
  anchorEl,
  open,
  onClose,
  items,
  children,
  width,
  minWidth = 160,
  shadow = "headerMenu",
  PaperProps = {},
  anchorOrigin = { vertical: "bottom", horizontal: "right" },
  transformOrigin = { vertical: "top", horizontal: "right" },
  sx = {},
  disableScrollLock = true,
  ...props
}) => {
  const isMenuOpen = Boolean(open ?? anchorEl);

  const mergedPaperProps = {
    ...PaperProps,
    sx: {
      borderRadius: borderRadiusPx.lg,
      boxShadow: customShadows[shadow] || customShadows.headerMenu,
      border: `1px solid ${colors.dashboard.cardBorder}`,
      backgroundColor: colors.background.paper,
      mt: 0.5,
      minWidth: minWidth,
      ...(width ? { width } : {}),
      "& .MuiMenuItem-root": {
        fontSize: "0.84rem",
        fontWeight: 500,
        color: colors.slate[700],
        py: 1,
        px: 2,
        transition: "all 0.15s ease-in-out",
        "&:hover": {
          backgroundColor: colors.slate[50],
          color: colors.slate[900],
        },
        "&.Mui-selected": {
          backgroundColor: colors.blue[50],
          color: colors.primary.main,
          fontWeight: 600,
          "&:hover": {
            backgroundColor: colors.blue[100],
          },
        },
      },
      "& .MuiDivider-root": {
        borderColor: colors.dashboard.divider,
        my: 0.5,
      },
      ...(PaperProps?.sx || {}),
    },
  };

  return (
    <Menu
      disableScrollLock={disableScrollLock}
      anchorEl={anchorEl}
      open={isMenuOpen}
      onClose={onClose}
      anchorOrigin={anchorOrigin}
      transformOrigin={transformOrigin}
      PaperProps={mergedPaperProps}
      sx={sx}
      {...props}
    >
      {items && items.length > 0
        ? items.map((item, index) => {
            if (!item) return null;

            if (item.divider) {
              return <Divider key={`divider-${index}`} sx={item.sx} />;
            }

            const itemTextColor = item.color || (item.danger ? colors.error.main : undefined);

            return (
              <MenuItem
                key={item.key || item.id || `menu-item-${index}`}
                onClick={(e) => {
                  if (item.onClick) item.onClick(e);
                  if (item.autoClose !== false && onClose) onClose(e);
                }}
                disabled={item.disabled}
                selected={item.selected}
                component={item.component}
                to={item.to}
                href={item.href}
                sx={{
                  ...(itemTextColor ? { color: `${itemTextColor} !important` } : {}),
                  ...item.sx,
                }}
              >
                {item.icon ? (
                  <ListItemIcon
                    sx={{
                      minWidth: 28,
                      color: itemTextColor || colors.slate[500],
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                ) : null}
                <ListItemText
                  primary={item.label || item.text}
                  primaryTypographyProps={{
                    fontSize: "0.84rem",
                    fontWeight: item.selected ? 600 : 500,
                  }}
                />
              </MenuItem>
            );
          })
        : children}
    </Menu>
  );
};

CommonMenu.propTypes = {
  anchorEl: PropTypes.any,
  open: PropTypes.bool,
  onClose: PropTypes.func,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string,
      id: PropTypes.string,
      label: PropTypes.node,
      text: PropTypes.node,
      onClick: PropTypes.func,
      icon: PropTypes.node,
      color: PropTypes.string,
      danger: PropTypes.bool,
      disabled: PropTypes.bool,
      divider: PropTypes.bool,
      selected: PropTypes.bool,
      autoClose: PropTypes.bool,
      component: PropTypes.any,
      to: PropTypes.string,
      href: PropTypes.string,
      sx: PropTypes.object,
    })
  ),
  children: PropTypes.node,
  width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  minWidth: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  shadow: PropTypes.string,
  PaperProps: PropTypes.object,
  anchorOrigin: PropTypes.object,
  transformOrigin: PropTypes.object,
  sx: PropTypes.object,
};

export default CommonMenu;
